<template>
  <div class="chat-container">
    <!-- Header do Chat Modernizado -->
    <div class="chat-header">
      <div class="header-left">
        <v-btn
          icon
          @click="$router.push({name: 'tickets'})"
          class="back-btn"
          color="white"
        >
          <v-icon>arrow_back</v-icon>
        </v-btn>

        <div class="user-avatar">
          <div class="avatar-circle">
            <v-icon color="white" size="20">person</v-icon>
          </div>
          <div class="online-indicator"></div>
        </div>

        <div class="chat-info">
          <div class="chat-title">
            Chamado #{{ticket.id}}
          </div>
          <div class="chat-subtitle">
            {{ticket.subject}}
          </div>
          <div v-if="ticket.sale_code" class="sale-code-info">
            VENDA: {{ticket.sale_code}}
          </div>
        </div>
      </div>

      <div class="header-right">
        <div class="status-badge" :class="`status-${ticket.status}`">
          {{ getStatusText(ticket.status) }}
        </div>

        <div class="chat-actions">
          <v-btn icon small color="white" class="action-btn">
            <v-icon size="18">more_vert</v-icon>
          </v-btn>
        </div>
      </div>
    </div>

    <!-- Container Principal de Mensagens -->
    <div class="messages-container" ref="messagesContainer">
      <div class="messages-background"></div>

      <!-- Data de início do chat -->
      <div class="chat-date-divider">
        <div class="date-bubble">
          {{ formatChatDate(ticket.created_at) }}
        </div>
      </div>

      <!-- Mensagens -->
      <div class="messages-list">
        <div
          v-for="(message, index) in messages"
          :key="message.id"
          class="message-item"
          :class="{ 'message-own': isOwnMessage(message) }"
        >
          <message
            :id="message.id"
            :message="message.comment"
            :userName="message.user.name"
            :userType="message.commentable_type"
            :created_at="message.created_at"
            :files="message.files"
            :isFile="message.is_file"
            :isOwn="isOwnMessage(message)"
          ></message>
        </div>
      </div>
    </div>

    <!-- Área de Input Modernizada -->
    <div class="chat-input-container" v-if="ticket.status != 'closed'">
      <message-sender></message-sender>
    </div>

    <!-- Mensagem de Finalizado -->
    <div v-else class="finished-notice">
      <div class="finished-card">
        <div class="finished-icon">
          <v-icon color="grey" size="24">lock</v-icon>
        </div>
        <div class="finished-text">
          <div class="finished-title">Chat Encerrado</div>
          <div class="finished-subtitle">
            Este chamado foi finalizado. Para continuar o atendimento, abra um novo ticket.
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import moment from "moment";
import { createSubscription } from "@/services/flowit/websocket";

export default {
  name: "TicketRoom",
  data() {
    return {
      pingInterval: null,
      lastPingTime: null,
      ticketSubscription: null
    };
  },
  created() {
    const self = this;
    this.$store.dispatch("system/fetchSystemConfiguration");

    // Initialize WebSocket subscription with automatic token management
    createSubscription(
      'TicketChannel',
      { ticketRoomId: this.$route.params.id },
      {
        connected: function() {
          // Start ping system when connected
          self.startWebSocketPing();
        },
        disconnected: function() {
          // Stop ping system when disconnected
          self.stopWebSocketPing();
        },
        received: function(data) {

          // Handle ping/pong responses
          if (data && data.type === 'pong') {
            const now = Date.now();
            const latency = self.lastPingTime ? now - self.lastPingTime : 0;
            // WebSocket pong received - Latency calculated
            return;
          }

          // Handle regular messages
          if (data && data.message) {
            self.$store.dispatch("tickets/addToMessagesList", data.message);
            // Scroll to bottom when new message arrives
            self.$nextTick(() => {
              self.scrollToBottom();
            });
          }
        }
      }
    ).then(function(subscription) {
      self.ticketSubscription = subscription;
    }).catch(function(error) {
      console.error("❌ Failed to connect to ticket room:", error);
      // Tentar reconectar após um delay
      setTimeout(() => {
        self.retryConnection();
      }, 5000);
    });
  },
  beforeDestroy() {
    // Stop ping system
    this.stopWebSocketPing();

    // Cleanup subscription when component is destroyed
    if (this.ticketSubscription) {
      this.ticketSubscription.unsubscribe();
    }
  },
  computed: {
    ...mapGetters({
      ticket: "tickets/ticket",
      messages: "tickets/messages",
      systemAppName: "system/appName",
    }),
  },
  watch: {
    messages: {
      handler() {
        // Messages updated - debug logs removed for production
      },
      deep: true
    }
  },
  beforeCreate() {
    this.$store.dispatch("tickets/get", this.$route.params.id);
  },
  mounted() {
    this.$store.dispatch("tickets/disableTicketNotifications");
    this.scrollToBottom();
  },
  updated() {
    this.scrollToBottom();
  },
  components: {
    Message: () => import('@/components/tickets/Message.vue'),
    MessageSender: () => import("@/components/tickets/MessageSender.vue"),
  },
  methods: {
    retryConnection() {
      const self = this;
      createSubscription(
        'TicketChannel',
        { ticketRoomId: this.$route.params.id },
        {
          connected: function() {
            // Reconnected to ticket room
          },
          disconnected: function() {
            // Disconnected from ticket room
          },
          received: function(data) {
            if (data && data.message) {
              self.$store.dispatch("tickets/addToMessagesList", data.message);
              // Scroll to bottom when new message arrives
              self.$nextTick(() => {
                self.scrollToBottom();
              });
            }
          }
        }
      ).then(function(subscription) {
        self.ticketSubscription = subscription;
      }).catch(function(error) {
        console.error("❌ Reconnection failed:", error);
      });
    },
    getDate(date) {
      return moment(date);
    },
    getStatusColor(status) {
      const colors = {
        'opened': 'success',
        'pending': 'warning',
        'closed': 'grey',
        'Finalizado': 'error'
      };
      return colors[status] || 'primary';
    },
    getStatusText(status) {
      const statusMap = {
        'opened': 'Aberto',
        'pending': 'Pendente',
        'closed': 'Fechado',
        'Finalizado': 'Finalizado'
      };
      return statusMap[status] || status;
    },
    formatChatDate(date) {
      const today = moment();
      const messageDate = moment(date);

      if (messageDate.isSame(today, 'day')) {
        return 'HOJE';
      } else if (messageDate.isSame(today.subtract(1, 'day'), 'day')) {
        return 'ONTEM';
      } else {
        return messageDate.format('DD/MM/YYYY');
      }
    },
    isOwnMessage(message) {
      // Verificar se a mensagem é do usuário atual
      const currentUserId = this.$store.getters['account/userId'];
      
      return message.user && message.user.id === currentUserId;
    },
    scrollToBottom() {
      if (this.$refs.messagesContainer) {
        this.$refs.messagesContainer.scrollTop = this.$refs.messagesContainer.scrollHeight;
      }
    },

    // WebSocket ping system methods
    startWebSocketPing() {
      // Start WebSocket ping system every 30 seconds

      // Limpar qualquer ping anterior
      this.stopWebSocketPing();

      // Configurar novo ping a cada 30 segundos
      this.pingInterval = setInterval(() => {
        if (this.ticketSubscription && this.ticketSubscription.perform) {
          this.lastPingTime = Date.now();

          try {
            this.ticketSubscription.perform('ping', {
              timestamp: this.lastPingTime
            });
          } catch (error) {
            console.error('❌ Erro ao enviar ping:', error);
          }
        }
      }, 30000); // 30 segundos
    },

    stopWebSocketPing() {
      if (this.pingInterval) {
        clearInterval(this.pingInterval);
        this.pingInterval = null;
        this.lastPingTime = null;
      }
    },
  },
};
</script>

<style scoped>
/* Container Principal */
.chat-container {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f0f2f5;
  margin-left: 256px;
  width: calc(100vw - 256px);
  position: relative;
}

/* Header Modernizado */
.chat-header {
  background: var(--v-primary-base, linear-gradient(135deg, #1976d2 0%, #1565c0 100%));
  color: white;
  padding: 16px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  position: relative;
  z-index: 10;
  min-height: 80px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.back-btn {
  background: rgba(255, 255, 255, 0.1) !important;
  transition: all 0.3s ease;
}

.back-btn:hover {
  background: rgba(255, 255, 255, 0.2) !important;
}

.user-avatar {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.avatar-circle {
  width: 48px;
  height: 48px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid rgba(255, 255, 255, 0.3);
}

.online-indicator {
  position: absolute;
  bottom: 2px;
  right: 2px;
  width: 14px;
  height: 14px;
  background: #4caf50;
  border-radius: 50%;
  border: 2px solid white;
}

.chat-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.chat-title {
  font-size: 18px;
  font-weight: 600;
  line-height: 1.2;
  margin-bottom: 2px;
}

.chat-subtitle {
  font-size: 14px;
  opacity: 0.9;
  margin-bottom: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sale-code-info {
  font-size: 12px;
  background: rgba(255, 255, 255, 0.2);
  padding: 2px 8px;
  border-radius: 12px;
  display: inline-block;
  width: fit-content;
  margin-top: 4px;
}

.status-badge {
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.status-badge.status-opened {
  background: var(--v-success-lighten4, #e8f5e8);
  color: var(--v-success-darken2, #2e7d2e);
}

.status-badge.status-pending {
  background: var(--v-warning-lighten4, #fff3cd);
  color: var(--v-warning-darken2, #856404);
}

.status-badge.status-closed {
  background: var(--v-error-lighten4, #f8d7da);
  color: var(--v-error-darken2, #721c24);
}

.action-btn {
  background: rgba(255, 255, 255, 0.1) !important;
  transition: all 0.3s ease;
}

.action-btn:hover {
  background: rgba(255, 255, 255, 0.2) !important;
}

/* Container de Mensagens */
.messages-container {
  flex: 1;
  background: var(--v-background-base, #f0f2f5);
  overflow-y: auto;
  scroll-behavior: smooth;
  position: relative;
}

.messages-background {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background:
    linear-gradient(45deg, transparent 20%, rgba(255, 255, 255, 0.02) 20.5%, rgba(255, 255, 255, 0.02) 21%, transparent 21.5%),
    linear-gradient(-45deg, transparent 20%, rgba(255, 255, 255, 0.02) 20.5%, rgba(255, 255, 255, 0.02) 21%, transparent 21.5%);
  background-size: 20px 20px;
  z-index: 1;
}

.chat-date-divider {
  display: flex;
  justify-content: center;
  padding: 20px 0 10px 0;
  position: relative;
  z-index: 2;
}

.date-bubble {
  background: rgba(0, 0, 0, 0.1);
  color: #666;
  padding: 6px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.messages-list {
  padding: 0 16px 20px 16px;
  position: relative;
  z-index: 2;
}

.message-item {
  margin-bottom: 12px;
  display: flex;
  animation: fadeIn 0.3s ease-in-out;
  width: 100%;
}

.message-item.message-own {
  justify-content: flex-end;
}

.message-item:not(.message-own) {
  justify-content: flex-start;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Input Container */
.chat-input-container {
  background: white;
  border-top: 1px solid #e1e8ed;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.05);
}

/* Mensagem de Finalizado */
.finished-notice {
  background: white;
  border-top: 1px solid #e1e8ed;
  padding: 24px;
  display: flex;
  justify-content: center;
}

.finished-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 24px;
  background: #f8f9fa;
  border-radius: 16px;
  border: 1px solid #dee2e6;
  max-width: 400px;
}

.finished-icon {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  background: #e9ecef;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.finished-text {
  flex: 1;
}

.finished-title {
  font-size: 16px;
  font-weight: 600;
  color: #495057;
  margin-bottom: 4px;
}

.finished-subtitle {
  font-size: 14px;
  color: #6c757d;
  line-height: 1.4;
}

/* Scrollbar personalizada */
.messages-container::-webkit-scrollbar {
  width: 6px;
}

.messages-container::-webkit-scrollbar-track {
  background: transparent;
}

.messages-container::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 3px;
}

.messages-container::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.3);
}

/* Responsividade */
@media (max-width: 1200px) {
  .chat-container {
    margin-left: 0;
    width: 100vw;
  }
}

@media (max-width: 768px) {
  .chat-header {
    padding: 12px 16px;
    min-height: 70px;
  }

  .header-left {
    gap: 12px;
  }

  .avatar-circle {
    width: 42px;
    height: 42px;
  }

  .chat-title {
    font-size: 16px;
  }

  .chat-subtitle {
    font-size: 13px;
  }

  .sale-code-info {
    font-size: 11px;
    padding: 2px 6px;
  }

  .status-badge {
    padding: 4px 8px;
    font-size: 11px;
  }

  .messages-list {
    padding: 0 12px 16px 12px;
  }

  .message-item {
    margin-bottom: 10px;
  }

  .finished-card {
    padding: 16px 20px;
    margin: 0 16px;
  }

  .finished-title {
    font-size: 15px;
  }

  .finished-subtitle {
    font-size: 13px;
  }
}

@media (max-width: 480px) {
  .chat-header {
    padding: 10px 12px;
    min-height: 65px;
  }

  .header-left {
    gap: 10px;
  }

  .avatar-circle {
    width: 38px;
    height: 38px;
  }

  .online-indicator {
    width: 12px;
    height: 12px;
  }

  .chat-info {
    min-width: 0;
    overflow: hidden;
  }

  .chat-title {
    font-size: 15px;
  }

  .chat-subtitle {
    font-size: 12px;
  }

  .sale-code-info {
    font-size: 10px;
    padding: 1px 4px;
  }

  .status-badge {
    padding: 3px 6px;
    font-size: 10px;
  }

  .messages-list {
    padding: 0 8px 12px 8px;
  }

  .finished-card {
    flex-direction: column;
    text-align: center;
    gap: 12px;
    padding: 16px;
    margin: 0 12px;
  }

  .date-bubble {
    font-size: 11px;
    padding: 4px 8px;
  }
}
</style>
